import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { dashboardAPI, seedAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { TrendingUp, Package, ShoppingCart, Truck, DollarSign, RefreshCw, Database } from 'lucide-react';

export const Dashboard = () => {
  const [bestSellers, setBestSellers] = useState({ products: [], total_revenue: 0, total_profit: 0, total_units: 0 });
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sellersRes, summaryRes] = await Promise.all([
        dashboardAPI.getBestSellers(),
        dashboardAPI.getSummary(),
      ]);
      setBestSellers(sellersRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedAPI.seedData();
      toast.success('Sample data seeded successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <Layout>
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title" data-testid="dashboard-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your Amazon FBA business performance</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSeedData}
            disabled={seeding}
            data-testid="seed-data-btn"
          >
            <Database size={16} className="mr-2" />
            {seeding ? 'Seeding...' : 'Seed Sample Data'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid" data-testid="stats-grid">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label">Total Revenue</p>
              <p className="stat-value">{formatCurrency(bestSellers.total_revenue)}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <DollarSign size={24} className="text-blue-600" />
            </div>
          </div>
          <p className="stat-change positive">+12.5% from last month</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label">Total Profit</p>
              <p className="stat-value">{formatCurrency(bestSellers.total_profit)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <TrendingUp size={24} className="text-green-600" />
            </div>
          </div>
          <p className="stat-change positive">+8.2% from last month</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label">Units Sold</p>
              <p className="stat-value">{formatNumber(bestSellers.total_units)}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Package size={24} className="text-purple-600" />
            </div>
          </div>
          <p className="stat-change positive">+15.3% from last month</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-label">Pending Orders</p>
              <p className="stat-value">{summary.pending_orders || 0}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <ShoppingCart size={24} className="text-amber-600" />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">{summary.orders_count || 0} total orders</p>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Package size={20} className="text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{summary.products_count || 0}</p>
              <p className="text-sm text-slate-500">Products</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Truck size={20} className="text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{summary.shipments_count || 0}</p>
              <p className="text-sm text-slate-500">Shipments</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <DollarSign size={20} className="text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.revenue_today || 0)}</p>
              <p className="text-sm text-slate-500">Today's Revenue</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Best Sellers Table */}
      <Card data-testid="best-sellers-card">
        <CardHeader>
          <CardTitle className="text-lg">Best Selling Products</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead className="font-mono text-xs">EAN</TableHead>
                  <TableHead className="font-mono text-xs">ASIN</TableHead>
                  <TableHead className="text-right">Units Sold</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bestSellers.products.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{product.ean}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{product.asin}</TableCell>
                    <TableCell className="text-right">{formatNumber(product.units_sold)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(product.revenue)}</TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      {formatCurrency(product.profit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
};
